import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bookmark, Link2, Pencil, Trash2, Check, X, Wand2, Gauge, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { listPresets, renamePreset, deletePreset, errText } from "../lib/api";

const TONE_LABEL = (t) => (t || "modern").replace("_", " ");

const PresetRow = ({ p, onRename, onDelete, onUse }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(p.name);
  const [open, setOpen] = useState(false);
  const aff = p.affiliate?.enabled && p.affiliate?.url;

  const commit = async () => {
    if (!name.trim()) { toast.error("Name can't be empty"); return; }
    if (name.trim() !== p.name) await onRename(p.id, name.trim());
    setEditing(false);
  };

  return (
    <div data-testid={`preset-row-${p.id}`} className="rounded-2xl border border-white/10 bg-[#11141E] p-4 hover:border-white/20 transition-colors">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#FF453A]/12 border border-[#FF453A]/25">
          <Bookmark className="h-4 w-4 text-[#FF6A5E]" />
        </span>
        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="flex items-center gap-2">
              <Input data-testid={`preset-rename-input-${p.id}`} value={name} onChange={(e) => setName(e.target.value)} autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setName(p.name); setEditing(false); } }}
                className="h-8 bg-[#0B0C10] border-white/10 text-sm" />
              <button data-testid={`preset-rename-save-${p.id}`} onClick={commit} aria-label="Save name" className="h-8 w-8 rounded-full bg-[#FF453A] flex items-center justify-center hover:bg-[#FF5D54] transition-colors"><Check className="h-4 w-4 text-white" /></button>
              <button data-testid={`preset-rename-cancel-${p.id}`} onClick={() => { setName(p.name); setEditing(false); }} aria-label="Cancel" className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/15 transition-colors"><X className="h-4 w-4 text-white" /></button>
            </div>
          ) : (
            <h4 data-testid={`preset-name-${p.id}`} className="font-display font-bold text-sm text-white truncate">{p.name}</h4>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-400">
            <span className="capitalize">{TONE_LABEL(p.tone)}</span>
            {p.narration_speed && p.narration_speed !== 1 && (
              <span className="inline-flex items-center gap-1"><Gauge className="h-3 w-3" /> {p.narration_speed}× narration</span>
            )}
            {aff && (
              <span data-testid={`preset-affiliate-${p.id}`} className="inline-flex items-center gap-1 text-[#FF8A80]"><Link2 className="h-3 w-3" /> Affiliate link kept</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button data-testid={`preset-use-${p.id}`} size="sm" onClick={() => onUse(p)} className="h-8 rounded-full bg-[#FF453A] hover:bg-[#FF5D54] text-white text-xs px-3 transition-colors">
            <Wand2 className="h-3.5 w-3.5 mr-1" /> Use in Create
          </Button>
          <button data-testid={`preset-preview-toggle-${p.id}`} onClick={() => setOpen((o) => !o)} aria-label="Preview preset" className="h-8 w-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors">
            {open ? <ChevronUp className="h-4 w-4 text-slate-300" /> : <ChevronDown className="h-4 w-4 text-slate-300" />}
          </button>
          <button data-testid={`preset-rename-${p.id}`} onClick={() => setEditing(true)} aria-label="Rename preset" className="h-8 w-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors">
            <Pencil className="h-3.5 w-3.5 text-slate-300" />
          </button>
          <button data-testid={`preset-delete-${p.id}`} onClick={() => onDelete(p)} aria-label="Delete preset" className="h-8 w-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-red-500/80 transition-colors">
            <Trash2 className="h-3.5 w-3.5 text-slate-300" />
          </button>
        </div>
      </div>
      {open && (
        <dl data-testid={`preset-preview-${p.id}`} className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-xs border-t border-white/5 pt-4">
          {[["Tagline", p.tagline], ["Audience", p.audience], ["Hook", p.hook], ["Description", p.description]].map(([k, v]) => v ? (
            <div key={k} className={k === "Description" ? "sm:col-span-2" : ""}>
              <dt className="text-slate-500 uppercase tracking-wide text-[10px]">{k}</dt>
              <dd className="mt-0.5 text-slate-300 leading-relaxed">{v}</dd>
            </div>
          ) : null)}
          {aff && (
            <div className="sm:col-span-2">
              <dt className="text-slate-500 uppercase tracking-wide text-[10px]">Affiliate link</dt>
              <dd className="mt-0.5 text-[#FF8A80] break-all font-code">{p.affiliate.url}</dd>
            </div>
          )}
        </dl>
      )}
    </div>
  );
};

export const PresetManager = ({ refreshKey = 0 }) => {
  const navigate = useNavigate();
  const [presets, setPresets] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => { listPresets().then(setPresets).catch(() => setPresets([])); }, [refreshKey]);

  const onRename = async (id, name) => {
    try {
      const p = await renamePreset(id, name);
      setPresets((l) => l.map((x) => (x.id === id ? p : x)));
      toast.success("Preset renamed");
    } catch (e) { toast.error(errText(e)); }
  };
  const onDelete = async (p) => {
    try {
      await deletePreset(p.id);
      setPresets((l) => l.filter((x) => x.id !== p.id));
      toast.success(`Deleted "${p.name}"`);
    } catch (e) { toast.error(errText(e)); }
  };
  const onUse = (p) => navigate(`/studio?preset=${p.id}`);

  if (!presets || presets.length === 0) return null;

  return (
    <section data-testid="preset-manager" className="mt-10">
      <div className="flex items-center justify-between gap-4">
        <div>
          <span className="eyebrow">Your presets</span>
          <p className="mt-1 text-sm text-slate-400">Saved product briefs — reuse them for new narration and visuals while keeping the affiliate link.</p>
        </div>
        <button data-testid="preset-manager-toggle" onClick={() => setCollapsed((c) => !c)} className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1 transition-colors">
          {collapsed ? <>Show {presets.length} <ChevronDown className="h-3.5 w-3.5" /></> : <>Hide <ChevronUp className="h-3.5 w-3.5" /></>}
        </button>
      </div>
      {!collapsed && (
        <div className="mt-4 grid lg:grid-cols-2 gap-3">
          {presets.map((p) => <PresetRow key={p.id} p={p} onRename={onRename} onDelete={onDelete} onUse={onUse} />)}
        </div>
      )}
    </section>
  );
};
